import React, { useRef, useState } from 'react';
import { Button, Icon, message, Modal } from 'antd';
import { downloadFile } from '../../../../../helpers/functions';
import { getRateDescriptionTemplate } from '../../../../../services/rateService';
import './ImportModal.less';

const FALLBACK_TEMPLATE_FILENAME = 'Template_upload_rate_code_description.xlsx';
const ALLOWED_EXTENSIONS = ['xls', 'xlsx'];

const parseFilenameFromHeader = (disposition) => {
    if (!disposition) return null;
    const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
        } catch (e) {
            // fall through to plain match
        }
    }
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return match ? match[1].trim() : null;
};

const ImportModal = ({ visible, onClose, onUpload, title }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setIsUploading(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleClose = () => {
        if (isUploading || isDownloadingTemplate) return;
        reset();
        onClose && onClose();
    };

    const handleChooseFile = () => {
        inputRef.current && inputRef.current.click();
    };

    const handleFileChange = (e) => {
        const selected = e.target.files && e.target.files[0];
        if (!selected) return;
        const ext = selected.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            message.error('Chỉ hỗ trợ file .xls, .xlsx');
            e.target.value = '';
            return;
        }
        setFile(selected);
    };

    const handleDownloadTemplate = async () => {
        if (isDownloadingTemplate) return;
        setIsDownloadingTemplate(true);
        try {
            const result = await getRateDescriptionTemplate();
            const headers = (result && result.headers) || {};
            const filename =
                parseFilenameFromHeader(headers['content-disposition']) ||
                headers['x-suggestion-filename'] ||
                FALLBACK_TEMPLATE_FILENAME;
            const fileType =
                headers['content-type'] ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            downloadFile(result.data, filename, { type: fileType });
        } catch (err) {
            message.error('Tải tập tin mẫu thất bại. Vui lòng thử lại.');
        } finally {
            setIsDownloadingTemplate(false);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            message.warning('Vui lòng chọn tập tin');
            return;
        }
        if (!onUpload) {
            handleClose();
            return;
        }
        setIsUploading(true);
        try {
            await onUpload({ file });
            reset();
            onClose && onClose();
        } catch (err) {
            // parent is expected to surface its own error toast
        } finally {
            setIsUploading(false);
            reset();
        }
    };

    return (
        <Modal
            title={title || 'Tải lên'}
            visible={visible}
            onCancel={handleClose}
            maskClosable={false}
            width={620}
            className="rate-import-modal"
            footer={[
                <Button
                    key="close"
                    onClick={handleClose}
                    disabled={isUploading || isDownloadingTemplate}>
                    Đóng
                </Button>,
                <Button
                    key="upload"
                    type="primary"
                    onClick={handleSubmit}
                    loading={isUploading}
                    disabled={!file || isDownloadingTemplate}>
                    Tải lên
                </Button>,
            ]}>
            <div className="rate-import-modal__body">
                <div className="rate-import-modal__actions">
                    <Button onClick={handleChooseFile} disabled={isUploading}>
                        <Icon type="upload" /> Chọn tập tin
                    </Button>
                    <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloadingTemplate}
                        className={`rate-import-modal__template-link${isDownloadingTemplate ? ' is-loading' : ''
                            }`}>
                        <Icon type={isDownloadingTemplate ? 'loading' : 'download'} />
                        <span>
                            {isDownloadingTemplate ? 'Đang tải tập tin mẫu...' : 'Tải tập tin mẫu'}
                        </span>
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xls,.xlsx"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </div>

                {file && (
                    <div className="rate-import-modal__file-name">
                        <Icon type="paper-clip" /> {file.name}
                    </div>
                )}

                <div className="rate-import-modal__hint">Các định dạng hỗ trợ: .xls, .xlsx</div>
            </div>
        </Modal>
    );
};

export default ImportModal;
